import { useReducer, useEffect } from 'react';
import { Stitch, ActiveTool } from '../types/pattern';
import { STORAGE_KEYS } from '../utils/constants';

export type ToolState = {
  stitchType: string | null;
  color: string;
  selectedStitch: Stitch | null;
  activeTool: ActiveTool;
};

export type ToolAction =
  | { type: 'SELECT_STITCH_TYPE'; stitchType: string }
  | { type: 'SELECT_COLOR'; color: string }
  | { type: 'SELECT_STITCH'; stitch: Stitch | null }
  | { type: 'SET_TOOL'; tool: ActiveTool }
  | { type: 'CLEAR_SELECTION' };

function toolReducer(state: ToolState, action: ToolAction): ToolState {
  switch (action.type) {
    case 'SELECT_STITCH_TYPE': {
      const newType = state.stitchType === action.stitchType ? null : action.stitchType;
      return { ...state, stitchType: newType, selectedStitch: null, activeTool: null };
    }
    case 'SELECT_COLOR':
      return { ...state, color: action.color };
    case 'SELECT_STITCH':
      return { ...state, selectedStitch: action.stitch };
    case 'SET_TOOL': {
      const newTool = state.activeTool === action.tool ? null : action.tool;
      return { ...state, activeTool: newTool, stitchType: null, selectedStitch: null };
    }
    case 'CLEAR_SELECTION':
      return { ...state, selectedStitch: null };
    default:
      return state;
  }
}

function loadInitialToolState(): ToolState {
  const savedTool = localStorage.getItem(STORAGE_KEYS.ACTIVE_TOOL) as ActiveTool;
  return {
    stitchType: savedTool ? null : localStorage.getItem(STORAGE_KEYS.SELECTED_STITCH),
    color: localStorage.getItem(STORAGE_KEYS.SELECTED_COLOR) || '#382f2d',
    selectedStitch: null,
    activeTool: savedTool === 'eraser' || savedTool === 'recolor' ? savedTool : null,
  };
}

export function useToolState() {
  const [tool, dispatch] = useReducer(toolReducer, undefined, loadInitialToolState);

  // Persist tool state to localStorage
  useEffect(() => {
    if (tool.stitchType) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_STITCH, tool.stitchType);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_STITCH);
    }
    localStorage.setItem(STORAGE_KEYS.SELECTED_COLOR, tool.color);
    if (tool.activeTool) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_TOOL, tool.activeTool);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_TOOL);
    }
  }, [tool.stitchType, tool.color, tool.activeTool]);

  return { tool, dispatch };
}
